import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "@/lib/client-error-monitor";
import { toErrorDisplayMessage } from "@/lib/utils";

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = { hasError: boolean; error: Error | null };

/** Immer feste Texte — kein i18n, damit bei __store null nie weitere Fehler oder Rohkeys. */
const ERROR_TITLE = "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
const RETRY_BUTTON = "Erneut versuchen";

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown): State {
    if (error instanceof Error) {
      return { hasError: true, error };
    }
    return { hasError: true, error: new Error(toErrorDisplayMessage(error)) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportClientError("error_boundary", error, {
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg p-4 text-text">
          <div className="max-w-md text-center">
            <h1 className="mb-2 text-xl font-semibold text-danger">{ERROR_TITLE}</h1>
            <p className="mb-4 text-sm text-text-muted">
              {toErrorDisplayMessage(this.state.error.message)}
            </p>
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              className="rounded-lg bg-accent px-4 py-2 text-white hover:opacity-90"
            >
              {RETRY_BUTTON}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
